import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGT } from 'gt-react';
import { toast } from 'sonner';
import { orpc } from '@/api/rpc/client';
import Page from '@/components/page/Page';
import { Card } from '@/components/ui/card';
import { useCan } from '@/hooks/useCan';
import { Action, Subject } from '@erp/authz';
import { uploadPcscMasterlist } from '@/api/pricing/uploadPcscMasterlist';
import { MasterlistStatus } from './MasterlistStatus';
import { MasterlistUploadPanel } from './MasterlistUploadPanel';
import { uploadAgreementExtract } from '@/api/pricing/uploadAgreementExtract';
import { AgreementExtractPanel } from './AgreementExtractPanel';

/**
 * The PCSC Masterlist: the crossing between a vendor's own grouping codes and
 * Rexel's price class / subclass.
 *
 * Exactly one masterlist is in force, so this page has no list and no history —
 * it says what is loaded and lets someone replace it. Correcting a code in the
 * workbook and re-uploading changes what every SPA record resolves to
 * immediately, because the crossing is never stored against a SPA.
 */
export function PricingMasterlistPage() {
  const gt = useGT();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<number | null>(null);
  const [agreementProgress, setAgreementProgress] = useState<number | null>(
    null,
  );

  const [canUpload] = useCan(Action.Create, Subject.Pricing);
  const current = useQuery(
    orpc.pricing.masterlist.getCurrent.queryOptions({ input: {} }),
  );
  const currentAgreementExtract = useQuery(
    orpc.pricing.agreementExtract.getCurrent.queryOptions({ input: {} }),
  );

  const upload = useMutation({
    mutationFn: (file: File) => uploadPcscMasterlist(file, setProgress),
    onSuccess: async (summary) => {
      setProgress(null);
      // A degraded load is still a load, but it must not read as an unqualified
      // success — the warning on the card is the detail, this is the nudge.
      if (summary.degradedVendors.length > 0) {
        toast.warning(
          gt('Masterlist loaded, but no hierarchy codes for {vendors}', {
            vendors: summary.degradedVendors.join(', '),
            context: 'PCSC Masterlist page, degraded upload toast',
          }),
        );
      } else {
        toast.success(
          gt('Masterlist loaded: {count} price class crossings', {
            count: summary.rowCount,
            context: 'PCSC Masterlist page, upload success toast',
          }),
        );
      }
      await queryClient.invalidateQueries({
        queryKey: orpc.pricing.masterlist.getCurrent.key(),
      });
    },
    onError: (error: Error) => {
      setProgress(null);
      toast.error(error.message);
    },
  });
  const agreementUpload = useMutation({
    mutationFn: (file: File) =>
      uploadAgreementExtract(file, setAgreementProgress),
    onSuccess: async (summary) => {
      setAgreementProgress(null);
      toast.success(
        gt('Agreement extract loaded: {count} price-class rows', {
          count: summary.agreementRowCount,
          context: 'Pricing agreement extract upload success toast',
        }),
      );
      await queryClient.invalidateQueries({
        queryKey: orpc.pricing.agreementExtract.getCurrent.key(),
      });
      await queryClient.invalidateQueries({
        queryKey: orpc.pricing.spas.get.key(),
      });
    },
    onError: (error: Error) => {
      setAgreementProgress(null);
      toast.error(error.message);
    },
  });

  const masterlist = current.data?.masterlist ?? null;

  return (
    <Page
      title={gt('PCSC Masterlist', {
        context: 'PCSC Masterlist page title',
      })}
      subtitle={gt(
        'The crossing from a vendor’s grouping codes to Rexel price class / subclass.',
        { context: 'PCSC Masterlist page subtitle' },
      )}
    >
      <div className="flex flex-col gap-2">
        <Card className="p-3">
          <MasterlistStatus
            masterlist={masterlist}
            isLoading={current.isLoading}
            isError={current.isError}
          />
          {canUpload ? (
            <MasterlistUploadPanel
              hasMasterlist={masterlist !== null}
              isUploading={upload.isPending}
              progress={progress}
              onPick={(file) => upload.mutate(file)}
            />
          ) : null}
        </Card>
        <Card className="p-3">
          <AgreementExtractPanel
            summary={currentAgreementExtract.data?.agreementExtract ?? null}
            isLoading={currentAgreementExtract.isLoading}
            isError={currentAgreementExtract.isError}
            canUpload={canUpload}
            isUploading={agreementUpload.isPending}
            progress={agreementProgress}
            onPick={(file) => agreementUpload.mutate(file)}
          />
        </Card>
      </div>
    </Page>
  );
}
